import { useEffect, useRef, useState } from "react";
import Popup from "reactjs-popup";
import { isValidEmail } from "../../../utils/common";
import Icon from "../../core/Icon";
import type { EmailOption } from "../EmailContext";

function InputWithDropDown({
  emails = [],
  setEmails,
  fetchOptions,
}: {
  emails?: EmailOption[];
  setEmails: (
    emails: EmailOption[] | ((prev: EmailOption[]) => EmailOption[])
  ) => void;
  fetchOptions: (currentEmail: string) => Promise<EmailOption[]>;
}) {
  const popupRef = useRef<any>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [currentEmail, setCurrentEmail] = useState("");
  const [popupOpen, setPopupOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [options, setOptions] = useState<EmailOption[]>([]);
  const [loading, setLoading] = useState(false);

  // store refs for each email chip
  const emailRefs = useRef<(HTMLDivElement | null)[]>([]);
  // store refs for dropdown options
  const optionRefs = useRef<(HTMLButtonElement | null)[]>([]);

  // keep refs array length consistent
  useEffect(() => {
    emailRefs.current = emailRefs.current.slice(0, emails.length);
  }, [emails.length]);

  // keep option refs array length consistent
  useEffect(() => {
    optionRefs.current = optionRefs.current.slice(0, options.length);
  }, [options.length]);

  // Fetch options when a valid email is entered
  useEffect(() => {
    if (isValidEmail(currentEmail)) {
      const loadOptions = async () => {
        setLoading(true);
        try {
          const data = await fetchOptions(currentEmail);
          setOptions(data);
        } catch (err) {
          console.error("Error fetching email options:", err);
          setOptions([]);
        } finally {
          setLoading(false);
        }
      };

      loadOptions();
    } else {
      setOptions([]);
      setLoading(false);
    }
  }, [currentEmail, fetchOptions]);

  // reset selected index when popup closes or options change
  useEffect(() => {
    if (!popupOpen) {
      setSelectedIndex(-1);
    }
  }, [popupOpen]);

  // scroll selected option into view
  useEffect(() => {
    if (selectedIndex >= 0 && selectedIndex < optionRefs.current.length) {
      optionRefs.current[selectedIndex]?.scrollIntoView({
        block: "nearest",
        behavior: "smooth",
      });
    }
  }, [selectedIndex]);

  const focusInput = () => {
    inputRef.current?.focus();
  };

  const addEmail = (option: EmailOption) => {
    popupRef.current?.close();
    setCurrentEmail("");
    setEmails((prev: EmailOption[]) => [...prev, option]);
    setSelectedIndex(-1);
    // refocus input after adding
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Handle dropdown navigation when popup is open
    if (popupOpen && isValidEmail(currentEmail) && options.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex((prev) =>
          prev < options.length - 1 ? prev + 1 : prev
        );
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        return;
      }
      if (e.key === "Enter" && selectedIndex >= 0) {
        e.preventDefault();
        addEmail(options[selectedIndex]);
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        popupRef.current?.close();
        setSelectedIndex(-1);
        return;
      }
    }

    if (e.key === "Enter" || e.key === ",") {
      if (isValidEmail(currentEmail)) {
        e.preventDefault();
        addEmail({ email: currentEmail, name: currentEmail });
      }
    }

    if (e.key === "Backspace" && currentEmail === "") {
      // focus last email chip if exists
      const lastIdx = emails.length - 1;
      const lastChip = emailRefs.current[lastIdx];
      if (lastChip) {
        lastChip.focus();
        e.preventDefault();
      }
    }
  };

  const removeEmailAt = (index: number) => {
    setEmails((prev) => {
      const next = [...prev];
      next.splice(index, 1);
      return next;
    });
    // next tick: focus previous chip if exists else the input
    requestAnimationFrame(() => {
      const prevIdx = Math.min(Math.max(0, index - 1), emails.length - 2);
      const prevChip = emailRefs.current[prevIdx];
      if (prevChip) prevChip.focus();
      else focusInput();
    });
  };

  return (
    <>
      <Popup
        ref={popupRef}
        trigger={
          <div className="container-receipts">
            {emails?.length ? (
              <div className="emails-container">
                {emails.map((email, idx) => (
                  <div
                    key={email.email ?? idx}
                    className="emails"
                    tabIndex={0} // make it focusable
                    ref={(el) => {
                      emailRefs.current[idx] = el;
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Backspace") {
                        e.preventDefault();
                        removeEmailAt(idx);
                      }
                      if (e.key === "Enter") {
                        e.preventDefault();
                        removeEmailAt(idx);
                        focusInput();
                      }
                    }}
                    role="button"
                    aria-label={`Email ${email.email}`}>
                    <span style={{ marginRight: 8 }}>{email.email}</span>
                    <button
                      className="emails-close"
                      onClick={(ev) => {
                        ev.stopPropagation();
                        removeEmailAt(idx);
                      }}
                      aria-label={`Remove ${email.email}`}>
                      <Icon name="Close" />
                    </button>
                  </div>
                ))}
              </div>
            ) : null}
            <input
              ref={inputRef}
              type="text"
              className="input-header"
              value={currentEmail}
              autoFocus
              onKeyDown={handleInputKeyDown}
              // DON'T forcibly close popup here
              onChange={(e) => {
                const val = e.target.value;
                // open popup when the email becomes valid
                if (isValidEmail(val)) {
                  popupRef.current?.open();
                }
                setCurrentEmail(val);
              }}
            />
          </div>
        }
        closeOnDocumentClick
        repositionOnResize
        position={"bottom left"}
        className="mail-inbox-popup"
        onOpen={() => {
          setPopupOpen(true);
          // ensure input stays focused (some popup implementations steal focus)
          requestAnimationFrame(() => inputRef.current?.focus());
        }}
        onClose={() => {
          setPopupOpen(false);
        }}>
        {isValidEmail(currentEmail) ? (
          <div className="options-dropdown">
            {loading ? (
              // Skeleton loader
              <>
                {[1, 2, 3].map((i) => (
                  <div key={i} className="option-dropdown skeleton-loader">
                    <div className="skeleton-line skeleton-name"></div>
                    <div className="skeleton-line skeleton-email"></div>
                  </div>
                ))}
              </>
            ) : options.length > 0 ? (
              options.map((opt, idx) => {
                return (
                  <button
                    ref={(el) => {
                      optionRefs.current[idx] = el;
                    }}
                    className={`option-dropdown ${selectedIndex === idx ? "selected" : ""}`}
                    key={idx}
                    onBlur={(e) => {
                      e.stopPropagation();
                    }}
                    onFocus={(e) => {
                      e.stopPropagation();
                    }}
                    onMouseEnter={() => setSelectedIndex(idx)}
                    onMouseLeave={() => setSelectedIndex(-1)}
                    onClick={(e) => {
                      e.stopPropagation();
                      addEmail(opt);
                    }}
                    aria-selected={selectedIndex === idx}>
                    <h4>{opt.name}</h4>
                    {opt.email}
                  </button>
                );
              })
            ) : null}
          </div>
        ) : null}
      </Popup>
    </>
  );
}

export default InputWithDropDown;
